import type { ReactNode } from 'react';

import {
  CONTENT_ALIGN_PX,
  PAGE_HEADER_CLASS,
  PAGE_PADDING,
  PAGE_PB,
  PAGE_PT,
} from './layoutConstants';

interface PageContainerProps {
  children: ReactNode;
  /** Extra classes appended after the standard padding (e.g. `space-y-2`). */
  className?: string;
  /**
   * Set for pages whose content is plain text/cards rather than a table.
   *
   * A table's own cell padding carries its text out to CONTENT_ALIGN_PX, so
   * tables only need the plain gutter. Text laid directly in the container has
   * no such padding, and would otherwise sit ~17px left of every other page.
   */
  align?: boolean;
}

/**
 * Standard padded content region for a Content Tracker suite page.
 *
 * Every page previously picked its own gutter (px-4 / px-6 / px-8 / px-12 / p-4
 * / p-8), so content never lined up with the sidebar or with the breadcrumb
 * above it. Routing all pages through this component means the gutter is one
 * constant in `layoutConstants.ts`.
 */
export function PageContainer({ children, className = '', align = false }: PageContainerProps) {
  if (align) {
    return (
      <div
        className={`${PAGE_PT} ${PAGE_PB} pr-4 ${className}`.trim()}
        style={{ paddingLeft: `${CONTENT_ALIGN_PX}px` }}
      >
        {children}
      </div>
    );
  }
  return <div className={`${PAGE_PADDING} ${className}`.trim()}>{children}</div>;
}

interface PageHeaderProps {
  children: ReactNode;
  className?: string;
}

/**
 * Sticky header chrome shared by every suite page header.
 *
 * Collapses the four verbatim copies of
 * `sticky top-0 z-20 bg-white/95 backdrop-blur pb-3` into one definition, and
 * settles the z-10 vs z-20 disagreement in favour of z-10.
 */
export function PageHeader({ children, className = '' }: PageHeaderProps) {
  return <div className={`${PAGE_HEADER_CLASS} ${className}`.trim()}>{children}</div>;
}
